import { Dimension, VariantItemProps } from '@design-system/variant-selector';
import uniq from 'lodash/uniq';

import { ProductPage, Variants } from '~/templates/L2ProductPage/models';
import { SalsifyProductData } from '~/types/common';
import { FIELD_NAME_VARIANT_AND_ICON } from '~/utils/constants';

import { imageMapper } from '../image/imageMapper';

const variantDimensionMapper = (
  fieldsName: string,
  productData: SalsifyProductData
): string | Dimension => {
  if (fieldsName !== FIELD_NAME_VARIANT_AND_ICON)
    return productData[fieldsName];

  const { variantName, variantIcon } = productData.Variant_And_Icon;

  return {
    variantName,
    variantIcon: variantIcon
      ? imageMapper({
          image: {
            gatsbyImageData:
              variantIcon?.localAsset?.childImageSharp?.gatsbyImageData,
            url: variantIcon.url,
          },
          alt: variantIcon.alt,
        })
      : undefined,
  } as Dimension;
};

const extractFieldOptions = (
  variants: Pick<ProductPage, 'fields'>[],
  fieldsName: string[]
): VariantItemProps[] => {
  const validVariants = variants.filter(
    (variant) => variant.fields.productData
  );

  const dim2Variants = fieldsName[1]
    ? uniq(
        validVariants.map((variant) =>
          fieldsName[1] === 'Variant_And_Icon'
            ? variant.fields.productData[fieldsName[1]].variantName
            : variant.fields.productData[fieldsName[1]]
        )
      )
    : [];

  return validVariants.map((variant) => ({
    dimension1: variantDimensionMapper(
      fieldsName[0],
      variant.fields.productData
    ),
    ...(dim2Variants.length > 1 && {
      dimension2: variantDimensionMapper(
        fieldsName[1],
        variant.fields.productData
      ),
    }),
    href: variant.fields.fullPath,
  }));
};

export const variantSelectorMapper = (...props) => {
  const [firstProp] = props;
  const isPDPHeader = 'variantSelector' in firstProp;

  const variantSelector: Variants = isPDPHeader
    ? firstProp.variantSelector
    : firstProp;
  const productData: SalsifyProductData = isPDPHeader
    ? firstProp.productData
    : props[2]?.salsifyExternalData;

  if (!variantSelector?.variants?.length) return null;

  const {
    labels: [{ dimensions }],
    variants,
    singleVariant,
  } = variantSelector;

  return {
    currentDimension1: productData[dimensions[0].dimension],
    currentDimension2: productData[dimensions[1]?.dimension],
    labelDimension1: dimensions[0].label,
    labelDimension2: dimensions[1]?.label,
    variants: extractFieldOptions(
      variants,
      dimensions.map(({ dimension }) => dimension)
    ),
    singleVariant,
  };
};
